import { jsPDF } from "jspdf";
import _ from "lodash";
import {
  CategoryData,
  CategoryType,
  EatingHabitsData,
  ExerciseHistoryData,
  MentalHealthHistoryData,
  OtherWeightManagementHistoryData,
  WeightHistoryData,
  WeightTreatmentHistoryData,
} from "src/revamp/types/PatientFormTypes";
import { User } from "src/services/dto/Security";

import { formatPhoneNumber, getName } from "../helpers";
import { PlexFont, PoppinNormal } from "./constants";
import {
  PDFGeneratorState,
  drawExercise,
  drawFooter,
  drawHeader,
  drawRow,
  drawSectionTitle,
  drawTitle,
  drawValue,
  endSection,
  getDesc,
  getValue,
  listItems,
} from "./helper";

const drawPatientInfo = (state: PDFGeneratorState) => {
  drawRow(state, "Name:", getName(state.patient));
  drawRow(state, "Date of Birth:", state.patient.dateOfBirth);
  drawRow(state, "Gender:", state.patient.gender);
  drawRow(state, "Phone:", formatPhoneNumber(state.patient.phone));
  drawRow(state, "Email:", state.patient.email);

  endSection(state);
};

const drawWeightHistory = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Weight History");
  const weight = getValue(state, "weightHistory") as WeightHistoryData;

  drawValue(state, `Weight gain start: ${weight.weightGainStart}`);
  drawValue(state, `Weight gain influences: ${weight.weightGainInfluence.join(", ")}`);
  drawValue(state, `Weight trends: ${weight.weightTrend}`);
  if (weight.goalWeight) {
    drawValue(state, `Goal weight: ${weight.goalWeight} ${weight.goalWeightUnit}`);
  }
  drawValue(state, "Weight goal description:");
  listItems(state, weight.weightGoalDescription);

  endSection(state);
};

const drawWeightTreatmentHistory = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Weight Treatment History");
  const weightTreatment = getValue(state, "weightTreatmentHistory") as WeightTreatmentHistoryData;

  drawValue(state, "Medications tried:");
  listItems(state, weightTreatment.medications);
  drawValue(state, "Lifestyle programs tried:");
  listItems(state, weightTreatment.lifestylePrograms);
  drawValue(state, "Prior surgeries or procedures:");
  listItems(state, weightTreatment.priorSurgeriesOrProcedures);
  drawValue(state, "Logging:");
  listItems(state, weightTreatment.logging);

  endSection(state);
};

const drawExerciseHistory = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Exercise History");
  const exercise = getValue(state, "exerciseHistory") as ExerciseHistoryData;

  if (_.isEmpty(exercise.exercises)) {
    drawValue(state, "No exercise history reported");
  } else {
    exercise.exercises.forEach((exercise) => drawExercise(state, exercise));
  }

  endSection(state);
};

const drawEatingHabits = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Eating Habits");
  const eating = getValue(state, "eatingHabits") as EatingHabitsData;

  drawValue(state, `Daily meals and snacks: ${eating.mealsAndSnacks}`);
  drawValue(state, `Eating behaviors:`);
  listItems(state, eating.eatingBehaviors);
  drawValue(state, `Consumption in last 24 hours:`);
  listItems(state, eating.foodAndDrink.split(","));
  drawValue(state, "Eating disorders:");
  listItems(state, eating.eatingDisorders);

  endSection(state);
};

const drawMentalHealthHistory = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Mental Health History");
  const mentalHealth = getValue(state, "mentalHealthHistory") as MentalHealthHistoryData;

  drawValue(state, "Mental health concerns:");
  listItems(state, mentalHealth.mentalHealthConcern);
  drawValue(state, `Motivation and readiness: ${mentalHealth.motivationAndReadiness}`);

  endSection(state);
};

const drawAdditionalMedicalHistory = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Other Weight Management History");
  const otherWMHistory = getValue(
    state,
    "otherWeightManagementHistory",
  ) as OtherWeightManagementHistoryData;

  drawValue(state, "Family medical history:");
  listItems(state, otherWMHistory.medicalHistory);

  drawValue(state, `Type 2 Diabetes: ${otherWMHistory.type2Diabetes ? "Yes" : "No"}`);

  if (otherWMHistory.type2Diabetes) {
    drawValue(state, "Diabetes medications:");
    listItems(state, otherWMHistory.diabetesMedications);
    drawValue(state, `Checks blood sugar: ${otherWMHistory.checkBloodSugar ? "Yes" : "No"}`);
    const eyeDisease = otherWMHistory.eyeDisease;
    if (eyeDisease?.response) {
      drawValue(state, `Has eye disease related to diabetes: Yes${getDesc(eyeDisease, ", ")}.`);
    } else {
      drawValue(state, `Has eye disease related to diabetes: No`);
    }
  }

  drawValue(state, "Gastrointestinal problems:");
  listItems(state, otherWMHistory.gastrointestinalProblems);

  const surgery = otherWMHistory.stomachOrBowelSurgery;
  drawValue(
    state,
    `Has non-weight loss surgery of the stomach or bowel: ${
      surgery.response ? `Yes${getDesc(surgery, ", ")}` : "No"
    }`,
  );

  const upcomingSurgeries = otherWMHistory.upcomingSurgeries;
  drawValue(
    state,
    `Upcoming surgeries or procedures in the next 3 months: ${
      upcomingSurgeries.response ? `Yes${getDesc(upcomingSurgeries, ", ")}` : "No"
    }`,
  );

  if (!otherWMHistory.femaleHistory.includes("None")) {
    // hide since it may be non-applicable
    drawValue(state, "Female history:");
    listItems(state, otherWMHistory.femaleHistory);
  }

  endSection(state);
};

// Main generator function
export const generateWeightManagementPDF = (
  patient: User,
  formData: Record<CategoryType, CategoryData>,
): jsPDF => {
  // Initialize state
  const state: PDFGeneratorState = {
    doc: new jsPDF("p", "mm", "letter"),
    y: 12,
    patient,
    formData,
  };

  // Setup fonts
  state.doc.addFileToVFS("Poppins-SemiBold.ttf", PlexFont);
  state.doc.addFont("Poppins-SemiBold.ttf", "PoppinsBold", "normal");
  state.doc.addFileToVFS("Poppins-Regular.ttf", PoppinNormal);
  state.doc.addFont("Poppins-Regular.ttf", "PoppinsNormal", "normal");

  // Draw content
  drawHeader(state);
  drawTitle(state, "Weight Management Forms");

  drawPatientInfo(state);
  drawWeightHistory(state);
  drawWeightTreatmentHistory(state);
  drawExerciseHistory(state);
  drawEatingHabits(state);
  drawMentalHealthHistory(state);
  drawAdditionalMedicalHistory(state);

  drawFooter(state);

  return state.doc;
};
